import { Modal } from "@asurion-core/aui-react-modal";
import { Action } from "@asurion-core/mx-asurion-ui-react";
import { selectShowTimeoutWarning, setShowTimeoutWarning, useGetUserQuery, useGlobalDispatch } from "@ubif/util-store";
import { useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled from "styled-components";

const StyledFooter = styled(Modal.Footer)`
  padding-top: 1.5rem;
  border-top: 1px solid #d5d6da;
`;
const StyledSubtitle = styled(Modal.Subtitle)`
  padding-bottom: 1.5rem;
  white-space: pre-line;
`;

/* Hide the close button as per design, not supported by AUI */
const StyledModal = styled(Modal)`
  min-height: 0;
  & > button {
    display: none;
    visibility: hidden;
  }
`;

interface SessionTimeoutModalProps {
  onSwitchUser: () => void;
  expiry?: number;
  isStoreAccount: boolean;
}

export function SessionTimeoutModal({ onSwitchUser, expiry, isStoreAccount }: SessionTimeoutModalProps) {
  const { t } = useTranslation("root-config");
  const modalRef = useRef<HTMLDivElement>(null);
  const dispatch = useGlobalDispatch();
  const { data, refetch } = useGetUserQuery();
  const showModal = useSelector(selectShowTimeoutWarning);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (isStoreAccount && expiry) {
      timeout = setTimeout(() => dispatch(setShowTimeoutWarning(true)), expiry - Date.now() - 3 * 60 * 1000);
    }
    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [isStoreAccount, expiry]);

  if (!expiry || !showModal) {
    return null;
  }

  const onContinue = () => {
    refetch();
    dispatch(setShowTimeoutWarning(false));
  };

  const user = `${data?.user?.givenName} ${data?.user?.familyName}`;
  const time = expiry ? String(Math.ceil((expiry - Date.now()) / 60000)) : "0";

  return (
    <StyledModal ref={modalRef} isOpen={showModal} isSticky ariaLabel={t("EmployeeLogin")} onCloseModal={() => {}}>
      <Modal.Header>
        <Modal.Title>{t("TimeoutWarningTitle")}</Modal.Title>
        <StyledSubtitle>
          <Trans t={t} i18nKey="TimeoutWarningSubtitle" values={{ user, time }}>
            {"{{user}}"}, your session is expiring in <strong>{"{{time}}"} mins</strong>.<br />
            Continue your session or login as a different store employee.
          </Trans>
        </StyledSubtitle>
      </Modal.Header>
      <StyledFooter>
        <Action onClick={onContinue}>{t("ContinueSession")}</Action>
        <Action variant="outline" onClick={onSwitchUser}>
          {t("SwitchUser")}
        </Action>
      </StyledFooter>
    </StyledModal>
  );
}
